// eslint-disable-next-line no-useless-concat
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { GridColumn as Column, getSelectedState } from "@progress/kendo-react-grid";
import { Label } from "@progress/kendo-react-labels";
import { getUserCredential, getTenantCookieData } from "../../lib/cookies";
import { Button } from "@progress/kendo-react-buttons";
import { DEFAULT_SORT, VIRTUAL_PAGE_SIZE } from "../../constants/grid";
import { LoadingPanel } from "../../components/loadingPanel";
import DtuBatchLogs from "../common/dtuBatchLogs";
import { columnMenuProps } from "../../components/customColumnMenu";
import { applyFormat, executeGetColumnOrderInfo, applyDateFormat, getfilterDataString } from "../../utils/utils";
import { HeaderPage } from "../base/headerPage";
import userPositionApi from "./service";
import ManageCustomField from "../custom-fields/manage-custom-field";
import AddProviderPosition from "./addProviderPosition";
import UploadDtu from "../user/uploadDtu";
import { getter } from "@progress/kendo-react-common";
import { DialogComponent } from "src/components/dialog";
import { DELETE_BTN_ACTIONS } from "src/constants/constants";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import UpdateProviderPosition from "./updateProviderPosition";
import { updateColumnReorderAsync } from "src/lib/common-service";
import { ProviderRowRender } from "src/components/renderers";
import { filterBy } from "@progress/kendo-data-query";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import DataTableV2 from "src/components/dataTableV2";
import { Skeleton } from "@progress/kendo-react-indicators";
import { Link } from "react-router-dom";
import BusinessGroupPopup from "./businessGroupPopup";
import DeleteBusinessGroupImpactNotice from "./deleteBusinessGroupImpactNotice";

const DATA_ITEM_KEY = "primary_key";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);
const DEFAULT_MULTISELECT_VALUE = [{ text: "Select All", id: "Select All" }];

const normalizeMultiSelectValue = (value) => {
  return Array.isArray(value) && value.length > 0 ? value : DEFAULT_MULTISELECT_VALUE;
};

const CompData = React.memo(
  (props) => {
    const {
      isAPICalling,
      initialLoading,
      data,
      pageData,
      dataCount,
      customColumn,
      dataItemKey,
      columns,
      defaultPageSize,
      pageSize,
      setPageSize,
      noDataFound,
      isExportDataClick,
      setExportData,
      exportData,
      resetSort,
      fileName,
      reorderable,
      initialFilter,
      initialSort,
      handleColumnMenu,
      columnReorderData,
      onSelectionChange,
      getExportData,
      onClearFilter,
      handlePageChange,
      refreshClick,
      rowRender,
      module,
      originalDataCount,
      defaultPageSizevs,
      originalData,
      setPageSizevs,
      pageSizevs,
      pageDatavs,
    } = props;

    const dataTableProps = useMemo(
      () => ({
        isAPICalling,
        initialLoading,
        data,
        pageData,
        dataCount,
        customColumn,
        columns,
        dataItemKey,
        width: "auto",
        defaultPageSize,
        pageSize,
        setPageSize,
        noDataFound,
        isExportDataClick,
        setExportData: setExportData,
        exportData,
        fileName,
        reorderable,
        initialFilter,
        initialSort,
        resetSort,
        handleColumnMenu,
        rowRender: rowRender,
        columnReorderData,
        onSelectionChange: onSelectionChange,
        getExportData,
        onClearFilter: onClearFilter,
        handlePageChange: handlePageChange,
        refreshClick: refreshClick,
        gridHeight: "auto",
        module,
        originalDataCount,
        defaultPageSizevs,
        originalData,
        setPageSizevs,
        pageSizevs,
        pageDatavs,
      }),
      [
        isAPICalling,
        initialLoading,
        data,
        pageData,
        dataCount,
        customColumn,
        columns,
        dataItemKey,
        defaultPageSize,
        pageSize,
        setPageSize,
        noDataFound,
        isExportDataClick,
        setExportData,
        exportData,
        fileName,
        reorderable,
        initialFilter,
        initialSort,
        resetSort,
        handleColumnMenu,
        columnReorderData,
        onSelectionChange,
        getExportData,
        onClearFilter,
        handlePageChange,
        refreshClick,
        rowRender,
        setPageSizevs,
        pageDatavs,
      ]
    );

    return (
      <>
        <DataTableV2 {...dataTableProps} />
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.data === nextProps.data &&
      prevProps.dataCount === nextProps.dataCount &&
      prevProps.isAPICalling === nextProps.isAPICalling &&
      prevProps.isSelection === nextProps.isSelection &&
      prevProps.isExportDataClick === nextProps.isExportDataClick &&
      prevProps.exportData === nextProps.exportData &&
      prevProps.columns === nextProps.columns &&
      prevProps.isDTUUploadReload === nextProps.isDTUUploadReload &&
      prevProps.initialFilter === nextProps.initialFilter &&
      prevProps.initialSort === nextProps.initialSort
    );
  }
);

const StringReadAndOnlyCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const val = dataItem[field || ""];

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
      <LoadingCell props={props} val={val} />
    </td>
  );
});

const BooleanCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const val = Number(dataItem[field]) ? "true" : "false";

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
      <LoadingCell props={props} val={val} />
    </td>
  );
});

const NumberCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const value = props.dataItem[props.field];
  const format = props.format;
  let formattedValue =
    dataItem[field] === null
      ? ""
      : dataItem[field] === "0.00000000" || dataItem[field] === ""
        ? ""
        : !isNaN(value)
          ? applyFormat(value, format)
          : value;

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
      <LoadingCell props={props} val={formattedValue} />
    </td>
  );
});

const DateCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const val =
    dataItem[field] && dataItem[field] !== "0000-00-00 00:00:00" ? applyDateFormat(dataItem[field], props.format) : "";

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
      <LoadingCell props={props} val={val} />
    </td>
  );
});

const cellType = (type) => {
  switch (type) {
    case "date":
      return DateCell;
    case "boolean":
      return BooleanCell;
    case "number":
      return NumberCell;
    default:
      return StringReadAndOnlyCell;
  }
};

const useCol = (columns) => {
  return useMemo(() => {
    return columns.map((column, idx) => {
      if (column.show && column.title !== "") {
        return (
          <Column
            key={idx}
            {...columnMenuProps(column)}
            sortable={true}
            width={"150px"}
            cell={cellType(column.type)}
            format={column.format}
          />
        );
      }
      return null;
    });
  }, [columns, cellType]);
};

const procareGetFilterValue = (data, alias) => {
  let tempArray = [];
  data.map((item) => {
    let str = item.id ? `a$${item.id}` : `a$is_null`;
    tempArray.push(str);
    return 0;
  });
  return tempArray.toString();
};

const getKeys = (key, selectedState) => {
  let keys = Object.keys(selectedState);
  let tempArray = [];
  keys.map((item) => {
    let item_Key = item.split("_");
    if (key === "provider") {
      tempArray.push(item_Key[0]);
    } else {
      tempArray.push(item_Key[1]);
    }
    return 0;
  });
  return tempArray;
};

const procareGetNullInFilter = (data, filterParamName, dataType = "string", alias = "a") => {
  if (!data || data.length === 0) return "";
  const nullKeyword = "a$is_null";
  let nullFilterStr = "";

  const idList = data.map((item) => item.id).join(",");

  if (idList.includes(nullKeyword)) {
    idList.replace(new RegExp(nullKeyword, "g"), "");
    nullFilterStr = ` AND (COALESCE(${alias}.${filterParamName}, ${
      dataType === "string" ? "''" : "0"
    }) ${dataType === "string" ? "= ''" : "= 0"})`;
  }

  return ` AND (${alias}.${filterParamName} IN (${idList}))${nullFilterStr}`;
};

const LoadingCell = (restProps) => {
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 10);
    return () => clearTimeout(timeout);
  }, []);

  if (isLoading) {
    return (
      <Skeleton
        shape="text"
        style={{
          width: "100%",
        }}
      />
    );
  }
  return <span title={restProps.val}>{restProps.val}</span>;
};

const ProviderPosition = (pageProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const divRef = React.createRef();
  const _ref = React.useRef(null);
  const [list, Action] = userPositionApi();
  const cookiesData = getUserCredential();
  const defaultPageSize = {
    skip: 0,
    recordTake: VIRTUAL_PAGE_SIZE,
  };
  const defaultPageSizevs = getTenantCookieData("record_per_page");
  const {
    setConfirmationPopup,
    confirmationPopup,
    specialtyDataList,
    specialtyDataFields,
    setSpecialtyDataFields,
    groupDataFields,
    groupDataList,
    setGroupDataFields,
    setEmployeeDataFields,
    employeeDataFields,
    employeeDataList,
    npiDataFields,
    npiDataList,
    setNpiDataFields,
    positionDataFields,
    positionDataList,
    setPositionDataFields,
    searchListLoading,
    data,
    columns,
    dataCount,
    setData,
    isAPICalling,
    noDataFound,
    initialLoading,
    setAPICallingState,
    loading,
    setLoadingStateSearch,
    setColumns,
    setSearchListLoading,
    setDataCount,
  } = list;
  const [selectedItem, setSelectedItem] = React.useState(location.state?.selectedItem || null);
  const [providerPlanItem, setProviderPlanItem] = React.useState(location.state?.providerPlanItem || null);
  const [providerSpeclityPlanItem, setProviderSpeclityPlanItem] = React.useState(
    location.state?.providerSpeclityPlanItem || null
  );
  const [providerSpecialtyItem, setProviderSpecialtyItem] = React.useState(
    location.state?.providerSpecialtyItem || null
  );
  const [dateRange, setDateRange] = React.useState(location.state?.dateRange || null);
  const [providerDashboardFilters, setProviderDashboardFilters] = React.useState(
    location.state?.providerDashboardFilters || null
  );

  const [dashboardFilter, setDashboardFilter] = React.useState("");
  const [dashboardGridFilter, setDashboardGridFilter] = React.useState({
    logic: "and",
    filters: [],
  });

  const [selectedState, setSelectedState] = React.useState({});
  const [isShow, setShow] = React.useState({
    isManageCustomType: false,
    exportCurrentVersion: false,
    exportAllVersion: false,
  });

  const [filterData, setFilterData] = React.useState({
    isSort: false,
    isFilter: false,
    data: "",
    in_sort_number: "org_position_key",
    sort_order: DEFAULT_SORT.order,
  });
  const [columnsData, setColumnsData] = React.useState([]);
  const [isCheckColumn, setColumnCheck] = React.useState(false);
  const [specialtyValue, setSpecialtyValue] = React.useState([]);
  const [groupValue, setGroupValue] = React.useState([]);
  const [employeeValue, setEmployeeValue] = React.useState([]);
  const [npiValue, setNpiValue] = React.useState([]);
  const [positionValue, setPositionValue] = React.useState([]);
  const [editData, setEditData] = React.useState({
    isEdit: false,
    data: "",
  });
  const [filterField, setFilterField] = React.useState({
    logic: "and",
    filters: [],
  });
  const [sortField, setSortField] = React.useState([]);
  const [mode, setMode] = React.useState(location.state?.openAddForm ? "Add" : "Default");
  const [resetSort, setResetSort] = React.useState(false);
  const [pageData, setPage] = React.useState({
    skip: 0,
    take: defaultPageSize.recordTake,
  });

  const [pageSize, setPageSize] = React.useState(defaultPageSizevs);
  const [exportDataClick, setExportDataClick] = React.useState(false);
  const [exportData, setExportData] = React.useState([]);
  const [deleteSelectedIds, setDeleteSelectedIds] = React.useState([]);
  const [gridColumns, setGridColumns] = React.useState([]);
  const [isUpdate, setIsUpdate] = React.useState(false);
  const [isOneEffVersion, setIsOneEffVersion] = React.useState(false);
  const [isSearchClick, setSearchClick] = React.useState(false);
  const [isPageApplied, setisPageApplied] = React.useState(false);
  const [loadState, setLoadState] = React.useState({
    isUploadState: false,
  });
  const [selectedRowId, setSelectedRowId] = React.useState("");
  const [searchFields, setSearchFields] = React.useState({});
  const [isAdd, setAdd] = React.useState(false);
  const [isSelection, setSelection] = React.useState(false);
  const [isDTUUploadReload, setDtUUploadReload] = React.useState(false);
  const [pageDatavs, setPagevs] = React.useState({
    skip: 0,
    take: defaultPageSizevs.recordTake,
  });
  const [gridData, setGridData] = React.useState([]);
  const [isBackBtnClick, setBackBtnClick] = React.useState(false);
  const [isFilterDataFetching, setIsFilterDataFetching] = React.useState(false);
  const [gridResetVersion, setGridResetVersion] = React.useState(0);
  const [viewBusinessGroupsData, setViewBusinessGroupsData] = React.useState([]);
  const [viewBusinessGroupsReqData, setViewBusinessGroupsReqData] = React.useState({
    in_position_key: "",
    in_page_row_offset: 0,
    in_page_rows: 99999,
  });
  const [isBusinessGroupListAPILoading, setIsBusinessGroupListAPILoading] = React.useState(false);

  const lastRedirectStateRef = React.useRef("");
  const appliedDashboardRedirectRef = React.useRef(false);
  const hasInitializedListStateRef = React.useRef(false);
  const isInitializingListStateRef = React.useRef(false);

  const getInFilter = () => {
    return filterData.isFilter ? filterData.data : null;
  };

  const getInSortNumber = () => {
    return filterData.isSort ? filterData.in_sort_number : "org_position_key";
  };

  const getInSortOrder = () => {
    return filterData.isSort ? filterData.sort_order : DEFAULT_SORT.order;
  };

  const escapeSqlValue = (value) => (value ? String(value).replace(/'/g, "''") : "");

  const buildProviderPositionGridFilterString = (columnMenuData) => {
    let filterString = getfilterDataString(columnMenuData, gridColumns, "provider-position");

    columnMenuData.forEach((filterItem) => {
      (filterItem.filters || []).forEach((filter) => {
        if (filter?.field !== "comp_plans" || filter?.operator !== "eq") {
          return;
        }

        const escapedValue = escapeSqlValue(filter.value);
        if (!escapedValue) {
          return;
        }

        const eqClause = `comp_plans = '${escapedValue}'`;
        const findInSetClause = `FIND_IN_SET('${escapedValue}', comp_plans) > 0`;
        filterString = filterString.replace(eqClause, findInSetClause);
      });
    });

    return filterString;
  };

  const buildDashboardGridFilter = (filterStringDashboardData = "") => {
    const gridFilter = { logic: "and", filters: [] };
    if (!filterStringDashboardData) {
      return gridFilter;
    }

    const filterGroups = [];

    const compPlansFindInSetMatch = filterStringDashboardData.match(
      /FIND_IN_SET\('((?:''|[^'])*)',\s*comp_plans\)\s*>\s*0/i
    );
    const compPlansLikeMatch = filterStringDashboardData.match(/comp_plans\s+LIKE\s+'%((?:''|[^'])*)%'/i);
    const compPlansValue = (compPlansFindInSetMatch?.[1] || compPlansLikeMatch?.[1] || "").replace(/''/g, "'");
    if (compPlansValue) {
      filterGroups.push({
        logic: "and",
        filters: [
          {
            field: "comp_plans",
            operator: "eq",
            value: compPlansValue,
          },
        ],
      });
    }

    const betweenDateMatch = filterStringDashboardData.match(
      /DATE\(([^)]+)\)\s+BETWEEN\s+DATE_SUB\(CURDATE\(\),\s+INTERVAL\s+12\s+MONTH\)\s+AND\s+CURDATE\(\)/i
    );
    if (betweenDateMatch) {
      const dateField = betweenDateMatch[1].trim();
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 12);
      filterGroups.push({
        logic: "and",
        filters: [
          {
            field: dateField,
            operator: "gte",
            value: startDate,
          },
          {
            field: dateField,
            operator: "lte",
            value: endDate,
          },
        ],
      });
    } else {
      const currentDateMatch = filterStringDashboardData.match(/DATE\(([^)]+)\)\s*<=\s*CURDATE\(\)/i);
      if (currentDateMatch) {
        const dateField = currentDateMatch[1].trim();
        filterGroups.push({
          logic: "and",
          filters: [
            {
              field: dateField,
              operator: "lte",
              value: new Date(),
            },
          ],
        });
      }
    }

    const hasDashboardActiveTerminationOrEmpty =
      /\(termination_date\s+IS\s+NULL\s+OR\s+termination_date\s*=\s*''\)/i.test(filterStringDashboardData);
    const hasLegacyTermDateIsNullOnly =
      !hasDashboardActiveTerminationOrEmpty && /termination_date\s+IS\s+NULL/i.test(filterStringDashboardData);
    if (hasLegacyTermDateIsNullOnly) {
      filterGroups.push({
        logic: "and",
        filters: [
          {
            field: "termination_date",
            operator: "isnull",
            value: true,
          },
        ],
      });
    }

    if (filterGroups.length > 0) {
      gridFilter.filters = filterGroups;
    }

    return gridFilter;
  };

  const combineFilterStrings = (...filters) => {
    return filters
      .map((filter) => (typeof filter === "string" ? filter.trim() : ""))
      .filter(Boolean)
      .join(" ");
  };

  const normalizeFilterValue = (value) => {
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = `${value.getMonth() + 1}`.padStart(2, "0");
      const day = `${value.getDate()}`.padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return value;
  };

  const areFilterDescriptorsEqual = (leftFilter, rightFilter) => {
    if (!leftFilter || !rightFilter) {
      return false;
    }

    const leftHasChildren = Array.isArray(leftFilter.filters);
    const rightHasChildren = Array.isArray(rightFilter.filters);

    if (leftHasChildren || rightHasChildren) {
      if (!leftHasChildren || !rightHasChildren) {
        return false;
      }

      if ((leftFilter.logic || "and") !== (rightFilter.logic || "and")) {
        return false;
      }

      if (leftFilter.filters.length !== rightFilter.filters.length) {
        return false;
      }

      return leftFilter.filters.every((childFilter, index) =>
        areFilterDescriptorsEqual(childFilter, rightFilter.filters[index])
      );
    }

    return (
      leftFilter.field === rightFilter.field &&
      leftFilter.operator === rightFilter.operator &&
      normalizeFilterValue(leftFilter.value) === normalizeFilterValue(rightFilter.value)
    );
  };

  const getUniqueFilters = (filters = []) => {
    return filters.reduce((uniqueFilters, currentFilter) => {
      if (!uniqueFilters.some((savedFilter) => areFilterDescriptorsEqual(savedFilter, currentFilter))) {
        uniqueFilters.push(currentFilter);
      }
      return uniqueFilters;
    }, []);
  };

  const getAdditionalGridFilters = (filters = []) => {
    const uniqueFilters = getUniqueFilters(filters);
    if (!dashboardGridFilter.filters.length) {
      return uniqueFilters;
    }

    return uniqueFilters.filter(
      (currentFilter) =>
        !dashboardGridFilter.filters.some((baseFilter) => areFilterDescriptorsEqual(baseFilter, currentFilter))
    );
  };

  const buildApiCallReq = ({
    filterState = filterData,
    pageState = pageDatavs,
    appliedSearchFields = searchFields,
  } = {}) => {
    const normalizedSearchFields = {
      groupValue: normalizeMultiSelectValue(appliedSearchFields?.groupValue),
      specialtyValue: normalizeMultiSelectValue(appliedSearchFields?.specialtyValue),
      employeeValue: normalizeMultiSelectValue(appliedSearchFields?.employeeValue),
      npiValue: normalizeMultiSelectValue(appliedSearchFields?.npiValue),
      positionValue: normalizeMultiSelectValue(appliedSearchFields?.positionValue),
    };
    const { out_tenant_id, out_user_id, out_acct_encrypt_key } = cookiesData;

    return {
      in_tenant_id: out_tenant_id,
      in_acct_encrypt_key: out_acct_encrypt_key,
      in_user_key: out_user_id,
      in_user_role_filter: ">0",
      in_filter: filterState?.isFilter ? filterState.data : null,
      in_sort_number: filterState?.isSort ? filterState.in_sort_number : "org_position_key",
      in_sort_order: filterState?.isSort ? filterState.sort_order : DEFAULT_SORT.order,
      in_page_row_offset: pageState?.skip ?? 0,
      in_page_rows: pageState?.take ?? defaultPageSizevs.recordTake,
      in_export_flag: 0,
      in_is_current_flag: 1,
      in_title_filter:
        normalizedSearchFields.groupValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetNullInFilter(normalizedSearchFields.groupValue, "org_title_key", "number", "a"),
      in_specialty_filter:
        normalizedSearchFields.specialtyValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetNullInFilter(normalizedSearchFields.specialtyValue, "specialty_mstr_key", "number", "a"),
      in_employee_id_filter:
        normalizedSearchFields.employeeValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetFilterValue(
              normalizedSearchFields.employeeValue.filter((x) => x.text !== "Select All"),
              "a"
            ),
      in_npi_filter:
        normalizedSearchFields.npiValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetFilterValue(
              normalizedSearchFields.npiValue.filter((x) => x.text !== "Select All"),
              "a"
            ),
      in_position_filter:
        normalizedSearchFields.positionValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetNullInFilter(normalizedSearchFields.positionValue, "org_position_key", "number", "a"),
    };
  };

  const getAPICallReq = () => {
    return buildApiCallReq();
  };

  const memoizedData = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      })),
    [data, selectedState]
  );
  React.useEffect(() => {
    const selobj = selectedState;
    if (
      (pageData.skip === 0 || filterData.isFilter || filterData.isSort || isPageApplied) &&
      Object.keys(selobj).length === 0
    ) {
      setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
      setisPageApplied(false);
    } else {
      setGridData(memoizedData.slice(pageData.skip, pageData.skip + pageData.take));
    }
  }, [memoizedData]);

  React.useEffect(() => {
    if (columns.length > 0) {
      setColumnsData(columns);

      setGridColumns(columns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  React.useEffect(() => {
    if (isBackBtnClick) {
      setBackBtnClick(false);
      setPage({
        skip: 0,
        take: defaultPageSize.recordTake,
      });
      setPagevs({
        skip: 0,
        take: defaultPageSizevs.recordTake,
      });
      setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
    }
  }, [isBackBtnClick]);

  React.useEffect(() => {
    const handlePopState = (event) => {
      if (mode === "Edit") {
        event.preventDefault();
        setMode("Default");
        setEditData({
          isEdit: false,
          data: "",
        });
        setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [mode, memoizedData]);

  React.useEffect(() => {
    const parent = document.querySelector("#content-" + headerConfig.id);

    if (parent) {
      const exportAllVersion = parent.querySelector(".export-to-excel");
      const exportCurrentVersion = parent.querySelector(".export-to-excel1");
      const deleteRows = parent.querySelector(".delete-rows");
      exportAllVersion && exportAllVersion.disableKendoMenu();
      exportCurrentVersion && exportCurrentVersion.disableKendoMenu();
      deleteRows && deleteRows.disableKendoMenu();
      data.length && exportAllVersion && exportAllVersion.enableKendoMenu();
      data.length && exportCurrentVersion && exportCurrentVersion.enableKendoMenu();
      if (selectedRowId) {
        deleteRows && deleteRows.enableKendoMenu();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data, selectedRowId]);

  React.useEffect(() => {
    if (isCheckColumn) {
      if (pageProps.writeAccess === 0) {
        return false;
      }
      updateColumDataAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckColumn]);

  React.useEffect(() => {
    if (isFilterDataFetching && specialtyDataFields.specialtyDataValue.length > 0) {
      handleOpenDropdown("employee");
      handleOpenDropdown("npi");
      handleOpenDropdown("position");
      setIsFilterDataFetching(false);
      const isSelectAllDefault =
        specialtyDataFields.specialtyDataValue.length === 1 &&
        specialtyDataFields.specialtyDataValue[0]?.id === "Select All";
      if (!isSelectAllDefault) {
        setSpecialtyValue(specialtyDataFields.specialtyDataValue);
      }
    }
  }, [isFilterDataFetching, specialtyDataFields.specialtyDataValue]);

  const updateColumDataAsync = async () => {
    setLoadingStateSearch({ ...loading, updateColumn: true });
    let columnString = prepareColumnString(columnsData);
    let reqData = {
      in_db_name: "procare_system",
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter_logical_cond: "N",
      in_filter_label_cond: "N",
      in_column_info: columnString,
    };
    let res = await updateColumnReorderAsync(reqData, dispatch, history);
    if (res) {
      setLoadingStateSearch({ ...loading, updateColumn: false });
      setColumns(columnsData);
      setColumnCheck(false);
    } else {
      setLoadingStateSearch({ ...loading, updateColumn: false });
    }
  };

  React.useEffect(() => {
    if (isOneEffVersion) {
      getProviderList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOneEffVersion]);

  const getProviderList = async () => {
    setData([...data.filter((x) => x.primary_key !== selectedRowId)]);
    setIsOneEffVersion(false);
    setSearchListLoading(false);
    setAPICallingState(true);
    let reqData = await getAPICallReq();
    await Action.getListDataAsync(reqData);
    setSelectedRowId("");
    setDeleteSelectedIds([]);
    setSelectedState({});
    setSearchListLoading(false);
  };

  React.useEffect(() => {
    if (isUpdate) {
      getUpdateProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdate]);

  const getUpdateProviders = async () => {
    setIsUpdate(false);
    setLoadState((prevState) => ({ ...prevState, isUploadState: true }));
    fetchAllDataLists();
    let reqData = {
      ...getAPICallReq(),
    };
    let res = await Action.getListDataAsync(reqData, "update");
    setSelectedRowId("");
    setPageMenuState(res);
    setLoadState({ ...loadState, isUploadState: false });
    setSearchListLoading(false);
  };

  const setPageMenuState = (response) => {
    if (!deleteSelectedIds.length) {
      return false;
    }

    let ids = response.map((el) => el[DATA_ITEM_KEY]);

    let selectedKeys = [];
    let tempArray = [];
    let tempArrayData = [];

    Object.keys(selectedState).map((item) => {
      let obj = {};

      obj.id = Number(item);
      selectedKeys.push(item);
      tempArray.push(obj);

      return 0;
    });

    ids.map((item) => {
      if (selectedKeys.includes(item.toString())) {
        tempArrayData.push(item);
      }
      return 0;
    });

    let strSelectedKeys = "";

    if (tempArrayData.length > 0) {
      strSelectedKeys = parseInt(selectedKeys[0]);
    }
    setSelectedRowId(strSelectedKeys);
  };

  const getPageTake = () => {
    return pageDatavs.take;
  };
  const getPageSkip = () => {
    return pageDatavs.skip;
  };

  React.useEffect(() => {
    if (isAdd && !isSearchClick) {
      setAdd(false);
      setMode("Default");
      setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
      ["specialty", "employee", "npi", "position"].forEach(handleOpenDropdown);
    } else if (isAdd && isSearchClick) {
      setAdd(false);
      setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
      setMode("Default");
      addProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdd]);

  const fetchAllDataLists = async () => {
    await Promise.all([
      Action.getSpecialtyDataList(specialtyDataFields.specialtyDataValue, groupDataFields.groupDataValue, "add-update"),
      Action.getEmployeeDataList(specialtyDataFields.specialtyDataValue, groupDataFields.groupDataValue, "add-update"),
      Action.getNpiDataList(specialtyDataFields.specialtyDataValue, groupDataFields.groupDataValue, "add-update"),
      Action.getPostionDataList(specialtyDataFields.specialtyDataValue, groupDataFields.groupDataValue, "add-update"),
    ]);
  };

  const addProviders = async () => {
    setLoadState((prevState) => ({ ...prevState, isUploadState: true }));
    fetchAllDataLists();
    let reqData = await getAPICallReq();
    await Action.getListDataAsync(reqData);
    setLoadState({ ...loadState, isUploadState: false });
    setSearchListLoading(false);
  };
  React.useEffect(() => {
    const currentRedirectState = JSON.stringify({
      selectedItem: location.state?.selectedItem || null,
      providerPlanItem: location.state?.providerPlanItem || null,
      providerSpeclityPlanItem: location.state?.providerSpeclityPlanItem || null,
      providerSpecialtyItem: location.state?.providerSpecialtyItem || null,
      dateRange: location.state?.dateRange || null,
      providerDashboardFilters: location.state?.providerDashboardFilters || null,
      openAddForm: location.state?.openAddForm || false,
    });
    const hasRedirectState = !!(
      location.state?.selectedItem ||
      location.state?.providerPlanItem ||
      location.state?.providerSpeclityPlanItem ||
      location.state?.providerSpecialtyItem ||
      location.state?.dateRange ||
      location.state?.providerDashboardFilters ||
      location.state?.openAddForm
    );

    if (hasRedirectState && lastRedirectStateRef.current !== currentRedirectState) {
      lastRedirectStateRef.current = currentRedirectState;
      setSelectedItem(location.state?.selectedItem || null);
      setProviderPlanItem(location.state?.providerPlanItem || null);
      setProviderSpeclityPlanItem(location.state?.providerSpeclityPlanItem || null);
      setProviderSpecialtyItem(location.state?.providerSpecialtyItem || null);
      setDateRange(location.state?.dateRange || null);
      setProviderDashboardFilters(location.state?.providerDashboardFilters || null);
      setMode(location.state?.openAddForm ? "Add" : "Default");
    }

    if (isInitializingListStateRef.current) {
      return;
    }

    const incomingSelectedItem = location.state?.selectedItem || selectedItem;
    const incomingProviderPlanItem = location.state?.providerPlanItem || providerPlanItem;
    const incomingProviderSpeclityPlanItem = location.state?.providerSpeclityPlanItem || providerSpeclityPlanItem;
    const incomingProviderSpecialtyItem = location.state?.providerSpecialtyItem || providerSpecialtyItem;
    const incomingDateRange = location.state?.dateRange || dateRange;
    const incomingProviderDashboardFilters = location.state?.providerDashboardFilters || providerDashboardFilters;

    if (!hasRedirectState && hasInitializedListStateRef.current) {
      return;
    }

    if (location.state?.openAddForm) {
      hasInitializedListStateRef.current = true;
      history.replace(location.pathname, {});
      ["specialty", "employee", "npi", "position"].forEach(handleOpenDropdown);
      return;
    }

    if (
      incomingSelectedItem ||
      incomingProviderPlanItem ||
      incomingProviderSpeclityPlanItem ||
      incomingProviderSpecialtyItem ||
      incomingProviderDashboardFilters
    ) {
      history.replace(location.pathname, {});
    }

    const initPage = async () => {
      isInitializingListStateRef.current = true;
      if (incomingProviderDashboardFilters != null) {
        appliedDashboardRedirectRef.current = true;

        const f = incomingProviderDashboardFilters;
        const withSelectAll = (arr) =>
          Array.isArray(arr) && arr.length > 0 ? arr : [{ text: "Select All", id: "Select All" }];

        const appliedSpecialty = withSelectAll(f.specialtyValue);
        const appliedGroup = withSelectAll(f.groupValue);
        const appliedEmployee = withSelectAll(f.employeeValue);
        const appliedNpi = withSelectAll(f.npiValue);
        const appliedPosition = withSelectAll(f.positionValue);

        const applySpecialty = () => {
          setSpecialtyValue(appliedSpecialty);
          setSpecialtyDataFields((prev) => ({ ...prev, specialtyDataValue: appliedSpecialty }));
        };
        const applyGroup = () => {
          setGroupValue(appliedGroup);
          setGroupDataFields((prev) => ({ ...prev, groupDataValue: appliedGroup }));
        };
        const applyEmployee = () => {
          setEmployeeValue(appliedEmployee);
          setEmployeeDataFields((prev) => ({ ...prev, employeeDataValue: appliedEmployee }));
        };
        const applyNpi = () => {
          setNpiValue(appliedNpi);
          setNpiDataFields((prev) => ({ ...prev, npiDataValue: appliedNpi }));
        };
        const applyPosition = () => {
          setPositionValue(appliedPosition);
          setPositionDataFields((prev) => ({ ...prev, positionDataValue: appliedPosition }));
        };

        applySpecialty();
        applyGroup();
        applyEmployee();
        applyNpi();
        applyPosition();

        await Promise.all([
          Action.getSpecialtyDataList(null, null, "add-update").then(() => {
            applySpecialty();
            applyGroup();
          }),
          Action.getEmployeeDataList(appliedSpecialty, appliedGroup, "add-update").then(applyEmployee),
          Action.getNpiDataList(appliedSpecialty, appliedGroup, "add-update").then(applyNpi),
          Action.getPostionDataList(appliedSpecialty, appliedGroup, "add-update").then(applyPosition),
        ]);

        await onPositionClick("", false, {
          specialtyValue: appliedSpecialty,
          groupValue: appliedGroup,
          employeeValue: appliedEmployee,
          npiValue: appliedNpi,
          positionValue: appliedPosition,
        });
        setProviderDashboardFilters(null);
        return;
      }
      if (incomingSelectedItem) {
        appliedDashboardRedirectRef.current = true;
        await handleOpenDropdown("specialty");
        await Promise.all([handleOpenDropdown("employee"), handleOpenDropdown("npi"), handleOpenDropdown("position")]);

        let dateColumn = "eff_start_date";
        let keyCondition = "org_position_key > 0";

        if (incomingSelectedItem === "Hire Date - Current") {
          dateColumn = "hire_date";
          keyCondition = "provider_mstr_key > 0";
        } else if (incomingSelectedItem === "Terminated Date - Current") {
          dateColumn = "termination_date";
          keyCondition = "provider_mstr_key > 0";
        }

        let filterStringDashboardData;

        const termDateNullClause =
          incomingSelectedItem === "Terminated Date - Current" || incomingSelectedItem === "Hire Date - Current"
            ? ""
            : " AND (termination_date IS NULL OR termination_date = '')";

        if (
          (incomingSelectedItem === "Hire Date - Current" || incomingSelectedItem === "Terminated Date - Current") &&
          (incomingDateRange === "Rolling 12 Months" || !incomingDateRange)
        ) {
          filterStringDashboardData = `AND (DATE(${dateColumn}) BETWEEN DATE_SUB(CURDATE(), INTERVAL 12 MONTH) AND CURDATE()) AND ${keyCondition}${termDateNullClause}`;
        } else {
          filterStringDashboardData = `AND (DATE(${dateColumn}) <= CURDATE()) AND ${keyCondition}${termDateNullClause}`;
        }

        await onPositionClick(filterStringDashboardData, true);
        setSelectedItem(null);
        return;
      }

      if (incomingProviderPlanItem != null) {
        appliedDashboardRedirectRef.current = true;
        await handleOpenDropdown("specialty");
        await Promise.all([handleOpenDropdown("employee"), handleOpenDropdown("npi"), handleOpenDropdown("position")]);

        let dateColumn = "eff_start_date";
        let keyCondition = "org_position_key > 0";

        if (incomingProviderPlanItem.dateValue === "Hire Date - Current") {
          dateColumn = "hire_date";
          keyCondition = "provider_mstr_key > 0";
        } else if (incomingProviderPlanItem.dateValue === "Terminated Date - Current") {
          dateColumn = "termination_date";
          keyCondition = "provider_mstr_key > 0";
        }
        const isRollingTwelveMonths =
          incomingProviderPlanItem.dateValue === "Hire Date - Current" ||
          incomingProviderPlanItem.dateValue === "Terminated Date - Current";
        const seriesNameLower = (incomingProviderPlanItem.seriesName ?? "").toString().toLowerCase();
        const isTerminatedCompPlanDrill =
          incomingProviderPlanItem.dateValue === "Terminated Date - Current" || seriesNameLower === "terminated";
        const isHireDateCompPlanDrill =
          incomingProviderPlanItem.dateValue === "Hire Date - Current" || seriesNameLower === "new hire";
        const termDateNullClausePlan =
          isTerminatedCompPlanDrill || isHireDateCompPlanDrill
            ? ""
            : " AND (termination_date IS NULL OR termination_date = '')";
        const escapedCompPlanValue = escapeSqlValue(incomingProviderPlanItem.value);
        const filterStringDashboardData = isRollingTwelveMonths
          ? `AND (DATE(${dateColumn}) BETWEEN DATE_SUB(CURDATE(), INTERVAL 12 MONTH) AND CURDATE()) AND FIND_IN_SET('${escapedCompPlanValue}', comp_plans) > 0 AND ${keyCondition}${termDateNullClausePlan}`
          : `AND (DATE(${dateColumn}) <= CURDATE()) AND FIND_IN_SET('${escapedCompPlanValue}', comp_plans) > 0 AND ${keyCondition}${termDateNullClausePlan}`;

        await onPositionClick(filterStringDashboardData, true);
        setProviderPlanItem(null);
        return;
      }
      if (incomingProviderSpeclityPlanItem != null) {
        appliedDashboardRedirectRef.current = true;
        const selectedSpecialty = await handleOpenDropdown("specialty", incomingProviderSpeclityPlanItem.speciality);
        if (selectedSpecialty?.length) {
          setSpecialtyValue(selectedSpecialty);
          setSpecialtyDataFields((prev) => ({ ...prev, specialtyDataValue: selectedSpecialty }));
        }
        await Promise.all([handleOpenDropdown("employee"), handleOpenDropdown("npi"), handleOpenDropdown("position")]);

        const planSeriesLower = (incomingProviderSpeclityPlanItem.plan ?? "").toString().toLowerCase();
        const isTerminatedSpecialtyPlanDrill = planSeriesLower === "terminated";
        const isNewHireSpecialtyPlanDrill = planSeriesLower === "new hire";
        const termClauseSpecialtyPlan =
          isTerminatedSpecialtyPlanDrill || isNewHireSpecialtyPlanDrill
            ? ""
            : " AND (termination_date IS NULL OR termination_date = '')";
        const escapedSpecialtyValue = escapeSqlValue(incomingProviderSpeclityPlanItem.speciality);
        const escapedPlanValue = escapeSqlValue(incomingProviderSpeclityPlanItem.plan);
        const filterStringDashboardData = `AND (specialty_name = '${escapedSpecialtyValue}') AND FIND_IN_SET('${escapedPlanValue}', comp_plans) > 0 AND org_position_key > 0${termClauseSpecialtyPlan}`;

        await onPositionClick(filterStringDashboardData, true);
        setProviderSpeclityPlanItem(null);
        return;
      }

      if (incomingProviderSpecialtyItem != null) {
        appliedDashboardRedirectRef.current = true;
        const selectedSpecialty = await handleOpenDropdown("specialty", incomingProviderSpecialtyItem.speciality);
        if (selectedSpecialty?.length) {
          setSpecialtyValue(selectedSpecialty);
          setSpecialtyDataFields((prev) => ({ ...prev, specialtyDataValue: selectedSpecialty }));
        }
        await Promise.all([handleOpenDropdown("employee"), handleOpenDropdown("npi"), handleOpenDropdown("position")]);

        const escapedSpecialtyValue = escapeSqlValue(incomingProviderSpecialtyItem.speciality);
        const filterStringDashboardData = `AND (specialty_name = '${escapedSpecialtyValue}') AND org_position_key > 0`;

        await onPositionClick(filterStringDashboardData, true);
        setProviderSpecialtyItem(null);
      } else {
        if (appliedDashboardRedirectRef.current) {
          appliedDashboardRedirectRef.current = false;
          hasInitializedListStateRef.current = true;
          return;
        }
        hasInitializedListStateRef.current = true;
        ["specialty", "employee", "npi", "position"].forEach(handleOpenDropdown);
      }
    };

    initPage().finally(() => {
      isInitializingListStateRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.state,
    selectedItem,
    providerPlanItem,
    providerSpeclityPlanItem,
    providerSpecialtyItem,
    providerDashboardFilters,
  ]);

  const handleOpenDropdown = async (module, specialtyValue = null) => {
    switch (module) {
      case "specialty": {
        const result = await Action.getSpecialtyDataList(null, null, null, specialtyValue);
        return Array.isArray(result) ? result : undefined;
      }
      case "employee":
        await Action.getEmployeeDataList(specialtyDataFields.specialtyDataValue, groupDataFields.groupDataValue);
        break;
      case "npi":
        await Action.getNpiDataList(specialtyDataFields.specialtyDataValue, groupDataFields.groupDataValue);
        break;
      case "position":
        await Action.getPostionDataList(specialtyDataFields.specialtyDataValue, groupDataFields.groupDataValue);
        break;
      default:
        break;
    }
  };

  const onPositionClick = async (filterStringDashboardData = "", isDashboardFilter = false, explicitValues = null) => {
    setSearchClick(true);
    const hasDashboardFilter = isDashboardFilter && filterStringDashboardData !== "";
    const gridFilter = buildDashboardGridFilter(hasDashboardFilter ? filterStringDashboardData : "");
    if (hasDashboardFilter) {
      setGridResetVersion((prev) => prev + 1);
    }

    setFilterField(gridFilter);
    setDashboardGridFilter(gridFilter);
    setDashboardFilter(hasDashboardFilter ? filterStringDashboardData : "");

    setSortField([]);
    setPageSize(defaultPageSizevs);
    setPage({
      skip: 0,
      take: defaultPageSize.recordTake,
    });
    setPagevs({
      skip: 0,
      take: defaultPageSizevs.recordTake,
    });
    setDataCount(0);
    setSelectedRowId("");
    setDeleteSelectedIds([]);
    setSelectedState({});
    setData([]);
    setColumns([]);

    const initialFilterData = {
      isSort: false,
      isFilter: hasDashboardFilter,
      data: hasDashboardFilter ? filterStringDashboardData : "",
      in_sort_number: "org_position_key",
      sort_order: DEFAULT_SORT.order,
    };
    setFilterData(initialFilterData);

    setMode("Default");
    setLoadingStateSearch({ ...loading, isSearchLoader: true });
    const { out_tenant_id, out_user_id, out_acct_encrypt_key } = cookiesData;

    const {
      specialtyValue: appliedSpecialtyValue,
      groupValue: appliedGroupValue,
      employeeValue: appliedEmployeeValue,
      npiValue: appliedNpiValue,
      positionValue: appliedPositionValue,
    } = explicitValues || {
      specialtyValue,
      groupValue,
      employeeValue,
      npiValue,
      positionValue,
    };

    let apiCallData = {
      in_tenant_id: out_tenant_id,
      in_acct_encrypt_key: out_acct_encrypt_key,
      in_user_key: out_user_id,
      in_user_role_filter: ">0",
      in_export_flag: 0,
      in_is_current_flag: 1,
      in_filter: null,
      in_sort_number: "org_position_key",
      in_sort_order: DEFAULT_SORT.order,
      in_page_row_offset: 0,
      in_page_rows: defaultPageSizevs.recordTake,
      in_title_filter:
        appliedGroupValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetNullInFilter(appliedGroupValue, "org_title_key", "number", "a"),
      in_specialty_filter:
        appliedSpecialtyValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetNullInFilter(appliedSpecialtyValue, "specialty_mstr_key", "number", "a"),
      in_employee_id_filter:
        appliedEmployeeValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetFilterValue(
              appliedEmployeeValue.filter((x) => x.text !== "Select All"),
              "a"
            ),
      in_npi_filter:
        appliedNpiValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetFilterValue(
              appliedNpiValue.filter((x) => x.text !== "Select All"),
              "a"
            ),
      in_position_filter:
        appliedPositionValue.filter((x) => x.text !== "Select All").length === 0
          ? null
          : procareGetNullInFilter(appliedPositionValue, "org_position_key", "number", "a"),
    };

    if (appliedGroupValue.filter((x) => x.text !== "Select All").length > 0) {
      apiCallData.in_filter = " AND org_position_key  > 0 ";
    }

    if (appliedSpecialtyValue.filter((x) => x.text !== "Select All").length > 0) {
      apiCallData.in_filter = " AND org_position_key  > 0 ";
    }

    if (appliedNpiValue.filter((x) => x.text !== "Select All").length > 0) {
      apiCallData.in_filter = " AND provider_mstr_key  > 0 ";
    }

    if (appliedPositionValue.filter((x) => x.text !== "Select All").length > 0) {
      apiCallData.in_filter = " AND org_position_key  > 0 ";
    }

    if (appliedEmployeeValue.filter((x) => x.text !== "Select All").length > 0) {
      apiCallData.in_filter = " AND provider_mstr_key  > 0 ";
    }
    if (hasDashboardFilter) {
      apiCallData.in_filter = filterStringDashboardData;
    }

    setSearchFields({
      ...searchFields,
      employeeValue: appliedEmployeeValue,
      npiValue: appliedNpiValue,
      positionValue: appliedPositionValue,
      groupValue: appliedGroupValue,
      specialtyValue: appliedSpecialtyValue,
    });
    await Action.getListDataAsync(apiCallData);
  };

  const getGridData = async (reqData, module) => {
    setResetSort(false);
    let response = await Action.getListDataAsync(reqData, module);
    setPageMenuState(response);
  };

  const handlePageChange = async (event) => {
    const pageData = event.page;
    const vskip = pageData.skip;
    const vtake = pageData.take;

    if (event.syntheticEvent.target.props !== undefined) {
      setAPICallingState(true);
      const dataVal = {
        ...getAPICallReq(),
        in_page_row_offset: vskip,
        in_page_rows: vtake,
      };
      setisPageApplied(true);
      setPagevs({ skip: vskip, take: vtake });
      getGridData(dataVal, "page");
      if (_ref.current) {
        const scrollableElement = _ref.current.querySelector(".k-grid-content");
        if (scrollableElement) {
          scrollableElement.scrollTop = 0;
        }
      }
    } else {
      setPage({ skip: vskip, take: vtake });
      setGridData(memoizedData.slice(vskip, vskip + vtake));
    }
  };

  const onRefreshClick = () => {
    setAPICallingState(true);
    let reqData = getAPICallReq();
    getGridData(reqData);
  };

  const onRefreshOnManualUpload = async () => {
    if (isSearchClick) {
      setDtUUploadReload(true);
      setAPICallingState(true);
      let reqData = getAPICallReq();
      await getGridData(reqData);
      setDtUUploadReload(false);
    }
  };

  const onClearFilter = () => {
    const hasDashboardBaseFilter = dashboardFilter !== "";
    const baseGridFilter = hasDashboardBaseFilter ? dashboardGridFilter : { logic: "and", filters: [] };
    if (hasDashboardBaseFilter) {
      setGridResetVersion((prev) => prev + 1);
    }
    setFilterField(baseGridFilter);
    setAPICallingState(true);
    let dataVal = {
      ...getAPICallReq(),
      in_filter: hasDashboardBaseFilter ? dashboardFilter : null,
      in_sort_number: getInSortNumber(),
      in_sort_order: getInSortOrder(),
      in_page_row_offset: getPageSkip(),
      in_page_rows: getPageTake(),
    };
    setFilterData({
      ...filterData,
      isFilter: hasDashboardBaseFilter,
      data: hasDashboardBaseFilter ? dashboardFilter : "",
    });

    getGridData(dataVal);
    if (_ref.current) {
      const scrollableElement = _ref.current.querySelector(".k-grid-content");
      if (scrollableElement) {
        scrollableElement.scrollTop = 0;
      }
    }
  };

  const onRowDoubleClick = (props) => {
    setEditData({
      ...editData,
      isEdit: true,
      data: props.originalProps.dataItem,
    });
    setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
    setMode("Edit");
    setSelection(false);
    history.push(location.pathname, { ...location.state, editMode: true });
  };
  let CustomColumn = useCol(gridColumns);
  const providerPositionGridKey = useMemo(() => `provider-position-grid-${gridResetVersion}`, [gridResetVersion]);

  const handleColumnMenu = (columnMenuData, isFilter, isSort, filterObj) => {
    const effectiveColumnMenuData = isFilter ? getAdditionalGridFilters(columnMenuData) : columnMenuData;
    let filterSkip = getPageSkip();
    let filterTake = getPageTake();
    setAPICallingState(true);
    if (isSort) {
      setSortField(columnMenuData);
    } else {
      filterSkip = 0;
      filterTake = pageSize.recordTake;
      setFilterField(filterObj);
      setPagevs({ skip: filterSkip, take: filterTake });
    }
    let index = "org_position_key";
    if (data.length > 0 && columnMenuData[0] !== undefined) {
      index = columnMenuData[0].field;
    }

    let filterString = isFilter
      ? combineFilterStrings(dashboardFilter, buildProviderPositionGridFilterString(effectiveColumnMenuData))
      : filterData.data;

    isFilter = false;
    if (filterString !== "") {
      isFilter = true;
    }

    let sortDir = filterData.isSort && columnMenuData.length ? filterData.sort_order : DEFAULT_SORT.order;
    let sortNumber = filterData.isSort && columnMenuData.length ? filterData.in_sort_number : "org_position_key";

    isSort = !columnMenuData.length ? false : filterData.isSort;

    if (columnMenuData[0] !== undefined && columnMenuData[0].dir !== undefined) {
      isSort = true;
      sortDir = columnMenuData[0].dir.toUpperCase();
      sortNumber = index;
    }
    let checkPrPos = [];
    if (isFilter) {
      effectiveColumnMenuData.map((filterItem, j) => {
        if (filterItem?.filters) {
          const filters = filterItem.filters;
          let thisFilter = filters[0];
          let columnObj = gridColumns.find((o) => o.field === thisFilter.field);
          checkPrPos.push(columnObj);
        }
      });
    }

    if (checkPrPos.filter((x) => x.gname === "vw_provider_master" && x.gname === "vw_org_position").length > 0) {
      filterString = filterString;
    } else if (checkPrPos.filter((x) => x.gname === "vw_provider_master").length > 0) {
      filterString = filterString + " AND provider_mstr_key  > 0 ";
    } else if (checkPrPos.filter((x) => x.gname === "vw_org_position").length > 0) {
      filterString = filterString + " AND org_position_key  > 0 ";
    }

    setFilterData({
      ...filterData,
      isFilter: isFilter,
      isSort: isSort,
      data: filterString,
      in_sort_number: sortNumber,
      sort_order: sortDir,
    });

    let dataVal = {
      ...getAPICallReq(),
      in_filter: isFilter ? filterString : null,
      in_sort_number: sortNumber,
      in_sort_order: sortDir,
      in_page_row_offset: filterSkip,
      in_page_rows: filterTake,
    };
    getGridData(dataVal, !isFilter ? (isSort ? "update" : "update") : "filter");
    if (_ref.current) {
      const scrollableElement = _ref.current.querySelector(".k-grid-content");
      if (scrollableElement) {
        scrollableElement.scrollTop = 0;
      }
    }
  };

  const onDeleteConfirm = async () => {
    let newProviderArray = [];
    let newPositionArray = [];
    data.map((item) => {
      if (getKeys("provider", selectedState).includes(item.provider_mstr_key.toString())) {
        if (item.org_position_key.toString() === "0") {
          let obj = {
            id: item.provider_mstr_key,
            date_version: null,
            position_name: item.position_name,
          };
          newProviderArray.push(obj);
        }
      }

      if (
        getKeys("position", selectedState).includes(item.org_position_key.toString()) &&
        selectedState[item.provider_mstr_key + "_" + item.org_position_key] === true
      ) {
        if (item.org_position_key.toString() !== "0") {
          let obj = {
            id: item.org_position_key,
            date_version: null,
            position_name: item.position_name,
            provider_id: item.provider_mstr_key,
            provider_date_version: null,
          };
          newPositionArray.push(obj);
        }
      }

      return 0;
    });

    let deletePositionData = {
      in_user_id: cookiesData.out_user_id.toString(),
      in_org_position_key: newPositionArray,
      in_tenant_id: cookiesData.out_tenant_id,
    };

    let deleteProviderData = {
      in_user_id: cookiesData.out_user_id.toString(),
      in_provider_mstr_key: newProviderArray,
      in_tenant_id: cookiesData.out_tenant_id,
    };

    setConfirmationPopup({
      title: "Delete",
      desc: "Are you sure you want to delete this record?",
      isVisible: false,
      type: "",
      actionBtn: [],
      action: "Delete",
      data: [],
    });

    if (newProviderArray.length > 0) {
      setAPICallingState(true);
      let resProvider = await Action.deleteProvider(deleteProviderData, dispatch, history);
      if (resProvider) {
        let reqData = {
          ...getAPICallReq(),
          in_sort_number: "org_position_key",
        };
        setSelectedRowId("");
        setSelectedState({});
        setDeleteSelectedIds([]);
        setAPICallingState(true);
        await Action.getListDataAsync(reqData);
        setSelection(false);
      } else {
        setSelectedRowId("");
        setSelectedState({});
        setDeleteSelectedIds([]);
        setSelection(false);
      }
    }

    if (newPositionArray.length > 0) {
      setAPICallingState(true);
      let res = await Action.deletePosition(deletePositionData, dispatch, history);
      let reqData = {
        ...getAPICallReq(),
        in_sort_number: "org_position_key",
      };
      if (res) {
        setSelectedRowId("");
        setSelectedState({});
        setDeleteSelectedIds([]);
        setAPICallingState(true);
        await Action.getListDataAsync(reqData);
        setSelection(false);
      } else {
        setSelectedRowId("");
        setSelectedState({});
        setDeleteSelectedIds([]);
        setSelection(false);
      }
    }
  };

  const handleViewBusinessGroups = () => {
    setShow({
      ...isShow,
      viewBusinessGroups: true,
    });
  };

  const handleBusinessGroupColumnMenu = async (requestData = {}) => {
    if (!viewBusinessGroupsReqData?.in_position_key) {
      return;
    }

    const businessGroupReqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: requestData?.in_filter || null,
      in_sort_number: requestData?.in_sort_number || 8,
      in_sort_order: requestData?.in_sort_order || "DESC",
      in_page_row_offset: viewBusinessGroupsReqData.in_page_row_offset,
      in_page_rows: viewBusinessGroupsReqData.in_page_rows,
      in_position_key: viewBusinessGroupsReqData.in_position_key,
    };

    const businessGroupData = await Action.getBusinessGroupByPosition(businessGroupReqData);
    setViewBusinessGroupsData(businessGroupData);
  };

  const handleMenuItemClick = async (module) => {
    switch (module) {
      case "manageCustomType":
        setShow({
          ...isShow,
          isManageCustomType: !isShow.isManageCustomType,
        });
        break;
      case "dtuLogs":
        setShow({
          ...isShow,
          dtuLogs: !isShow.dtuLogs,
        });
        break;
      case "exportAllVersion":
        setShow({
          ...isShow,
          exportCurrentVersion: false,
          exportAllVersion: true,
        });
        setExportDataClick(true);
        break;
      case "exportCurrentVersion":
        setShow({
          ...isShow,
          exportAllVersion: false,
          exportCurrentVersion: true,
        });
        setExportDataClick(true);
        break;
      case "delete":
        let newPositionArray = [];

        data.map((item) => {
          if (item.org_position_key.toString() !== "0") {
            if (
              getKeys("position", selectedState).includes(item.org_position_key.toString()) &&
              selectedState[item.provider_mstr_key + "_" + item.org_position_key] === true
            ) {
              if (item.org_position_key.toString() !== "0") {
                let obj = {
                  id: item.org_position_key,
                  date_version: null,
                  position_name: item.position_name,
                  provider_id: item.provider_mstr_key,
                  provider_date_version: null,
                };
                newPositionArray.push(obj);
              }
            }
          }
        });
        if (newPositionArray.length > 0) {
          let businessGroupReqData = {
            in_tenant_id: cookiesData.out_tenant_id,
            in_filter: null,
            in_sort_number: 8,
            in_sort_order: "DESC",
            in_page_row_offset: 0,
            in_page_rows: 99999,
            in_position_key: newPositionArray.map((item) => item.id).toString(),
          };
          setIsBusinessGroupListAPILoading(true);
          let businessGroupData = await Action.getBusinessGroupByPosition(businessGroupReqData);
          setIsBusinessGroupListAPILoading(false);
          if (businessGroupData.data.length > 0) {
            const attachedBusinessGroupCount = businessGroupData.data.length;
            setViewBusinessGroupsReqData({
              in_position_key: businessGroupReqData.in_position_key,
              in_page_row_offset: businessGroupReqData.in_page_row_offset,
              in_page_rows: businessGroupReqData.in_page_rows,
            });
            setViewBusinessGroupsData(businessGroupData);
            setConfirmationPopup({
              title: "Delete",
              desc: (
                <DeleteBusinessGroupImpactNotice
                  attachedBusinessGroupCount={attachedBusinessGroupCount}
                  onViewBusinessGroups={handleViewBusinessGroups}
                  isSinglePosition={newPositionArray.length === 1}
                />
              ),
              isVisible: true,
              type: "",
              actionBtn: DELETE_BTN_ACTIONS(onCancelConfirm, onDeleteConfirm),
              action: "Delete",
              data: businessGroupData?.data || [],
            });
          } else {
            setConfirmationPopup({
              title: "Delete",
              desc: (
                <DeleteBusinessGroupImpactNotice
                  attachedBusinessGroupCount={0}
                  onViewBusinessGroups={handleViewBusinessGroups}
                  isSinglePosition={newPositionArray.length === 1}
                />
              ),
              isVisible: true,
              type: "",
              actionBtn: DELETE_BTN_ACTIONS(onCancelConfirm, onDeleteConfirm),
              data: [],
              action: "Delete",
            });
          }
        } else {
          setConfirmationPopup({
            title: "Delete",
            desc: "Are you sure you want to delete this record?",
            isVisible: true,
            type: "",
            actionBtn: DELETE_BTN_ACTIONS(onCancelConfirm, onDeleteConfirm),
            data: [],
            action: "Delete",
            standAloneProvider: data.filter((x) => x.org_position_key.toString() === "0").length > 0,
          });
        }
        break;
      default:
        break;
    }
  };

  const getExportData = async () => {
    setExportData([]);
    let apiCallData = {
      ...getAPICallReq(),
      in_is_current_flag: isShow.exportAllVersion ? 0 : 1,
      in_page_rows: pageDatavs.take,
      in_export_flag: 1,
    };

    if (filterData.isFilter === false) {
      if (searchFields.groupValue.filter((x) => x.text !== "Select All").length > 0) {
        apiCallData.in_filter = " AND org_position_key  > 0 ";
      }

      if (searchFields.specialtyValue.filter((x) => x.text !== "Select All").length > 0) {
        apiCallData.in_filter = " AND org_position_key  > 0 ";
      }

      if (searchFields.npiValue.filter((x) => x.text !== "Select All").length > 0) {
        apiCallData.in_filter = " AND provider_mstr_key  > 0 ";
      }

      if (searchFields.positionValue.filter((x) => x.text !== "Select All").length > 0) {
        apiCallData.in_filter = " AND org_position_key  > 0 ";
      }

      if (searchFields.employeeValue.filter((x) => x.text !== "Select All").length > 0) {
        apiCallData.in_filter = " AND provider_mstr_key  > 0 ";
      }
    }

    let response = await Action.getExportListDataAsync(apiCallData);
    if (response.data.length > 0) {
      response.columData.map((item) => {
        if (item.type === "date") {
          response.data.forEach((rItem) => {
            if (rItem[item.field] === "0000-00-00 00:00:00" || rItem[item.field] === null || rItem[item.field] === "") {
              rItem[item.field] = null;
            } else {
              rItem[item.field] = new Date(applyDateFormat(rItem[item.field], item.format));
            }
          });
        }

        if (item.type === "number") {
          response.data.forEach((rItem) => {
            if (rItem[item.field] === "" || rItem[item.field] === "0.00000000" || rItem[item.field] === null) {
              rItem[item.field] = null;
            } else {
              let value = rItem[item.field].toString();
              if (value.includes(".")) {
                value = value.replace(/\.?0+$/, "");
              }
              rItem[item.field] = value === "" ? "0" : value;
            }
          });
        }

        if (item.type === "boolean") {
          response.data.forEach((rItem) => {
            rItem[item.field] = Number(rItem[item.field]) ? "true" : "false";
          });
        }
        return 0;
      });
      setExportData(response.data);
      setExportDataClick(false);
    }
  };

  const AddNewLink = React.memo(() => {
    return (
      <div>
        <a
          className="cta-small"
          onClick={(e) => {
            setMode("Add");
          }}
        >
          <i className="fas fa-plus"></i> Add New Provider Position
        </a>
      </div>
    );
  });

  const handleUpload = (e) => {
    setShow({
      ...isShow,
      dtuUpload: !isShow.dtuUpload,
    });
    e.preventDefault();
  };

  const BackBtn = () => {
    return (
      <>
        <Link to="/provider_dashboard" className="back-btn-link">
          <Button title="Back to Provider-Dashboard" type="submit" id="grid-back-btn" className="grid-back-btn">
            <i className="fa fa-arrow-left"></i>
          </Button>
        </Link>
      </>
    );
  };

  const headerConfig = {
    title: "Provider-Position",
    id: "provider-position",
    add_new_fx: AddNewLink,
    upload_fx: handleUpload,
    backBtn: { btn_fx: BackBtn },
    action_menu: {
      dtuLog: { fx: handleMenuItemClick },
      exportAllVersion: { fx: handleMenuItemClick },
      exportCurrentVersion: { fx: handleMenuItemClick },
      delete: { fx: handleMenuItemClick },
      manageType: {
        fx: handleMenuItemClick,
        items: [
          {
            caption: "Manage Custom Fields",
            module: "manageCustomType",
          },
        ],
      },
    },
    ...(pageProps.writeAccess === 1 && {
      manage_col: {
        columnsData: columnsData,
        setColumnsData: setColumnsData,
        setColumnCheck: setColumnCheck,
      },
    }),
  };

  const onSelectionChange = (event) => {
    setSelection(false);
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });

    let tempArray = [];

    Object.keys(newSelectedState).map((item) => {
      if (newSelectedState[item] === false) {
        return 0;
      }
      let obj = {};
      obj.id = item;
      obj.date_version = item.date_version;

      tempArray.push(obj);
      return 0;
    });

    const newSelectedStateKeys = Object.keys(newSelectedState);

    const selectedKeyArr = newSelectedStateKeys.filter(function (x) {
      return newSelectedState[x] !== false;
    });

    let newData = data.map((item) => ({
      ...item,
      selected: selectedKeyArr.includes(item.provider_mstr_key + "_" + item.org_position_key),
    }));
    setSelectedRowId(selectedKeyArr[0]);
    setData(newData);
    setSelectedState(newSelectedState);
    setDeleteSelectedIds(tempArray);
    setSelection(true);
  };

  const onCancelConfirm = () => {
    setConfirmationPopup({
      ...confirmationPopup,
      isVisible: false,
      type: "",
      actionBtn: [],
      data: [],
      action: "",
    });
    data.map((item) => {
      item.selected = false;
      return 0;
    });
    setData([...data]);
    setViewBusinessGroupsData([]);
    setViewBusinessGroupsReqData({
      in_position_key: "",
      in_page_row_offset: 0,
      in_page_rows: 99999,
    });
  };

  const prepareColumnString = (colData) => {
    return executeGetColumnOrderInfo(colData, "provider-position");
  };

  const columnReorderData = async (columnData) => {
    if (pageProps.writeAccess === 0) {
      return false;
    }
    const str = prepareColumnString(columnData);
    setLoadingStateSearch({ ...loading, updateColumn: true });
    let reqData = {
      in_db_name: "procare_system",
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter_logical_cond: "N",
      in_filter_label_cond: "N",
      in_column_info: str,
    };
    let res = await updateColumnReorderAsync(reqData, dispatch, history);
    if (res) {
      setLoadingStateSearch({ ...loading, updateColumn: false });
      let colDataList = JSON.parse(JSON.stringify(columnData));
      colDataList.forEach((item) => {
        item.hidden = false;
      });
      const mergedArray = columns.map((item1) => {
        const item2 = colDataList.find((item) => item.field === item1.field);
        return {
          ...item1,
          ...item2,
        };
      });
      setColumns([]);
      let colData = mergedArray.sort(function (a, b) {
        return a?.ariaColumnIndex - b?.ariaColumnIndex;
      });
      setColumnsData(colData);
      setColumns(colData);
    } else {
      setLoadingStateSearch({ ...loading, updateColumn: false });
    }
  };

  const customRowRender = (tr, props) => (
    <ProviderRowRender
      originalProps={props}
      tr={tr}
      onRowDoubleClick={onRowDoubleClick}
      title={"Double click to edit record"}
    />
  );

  const handleOnChange = async (e, name, setDataFields, dataList, dataFields) => {
    let dataValue = [];
    if (e.length === 1 && e.some((x) => x.text === "Select All")) {
      dataValue.push({ text: "Select All", id: "Select All" });
    } else {
      dataValue = [...e];
    }
    setDataFields({
      ...dataFields,
      [name]: dataValue,
    });

    if (name === "specialtyDataValue") {
      setSpecialtyValue(e);
      setGroupValue([]);
      setEmployeeValue([]);
      setNpiValue([]);
      setPositionValue([]);
      await Action.getGroupDataList(e);
      await Action.getEmployeeDataList(e);
      await Action.getNpiDataList(e);
      await Action.getPostionDataList(e);
    } else if (name === "groupDataValue") {
      setGroupValue(e);
      setEmployeeValue([]);
      setNpiValue([]);
      setPositionValue([]);
      await Action.getEmployeeDataList(specialtyDataFields.specialtyDataValue, e);
      await Action.getNpiDataList(specialtyDataFields.specialtyDataValue, e);
      await Action.getPostionDataList(specialtyDataFields.specialtyDataValue, e);
    } else if (name === "employeeDataValue") {
      setEmployeeValue(e);
    } else if (name === "npiDataValue") {
      setNpiValue(e);
    } else if (name === "positionDataValue") {
      setPositionValue(e);
    }
  };

  const filterChange = (event, setDataFields, moduleData, key) => {
    let dataValue = filterBy(moduleData.slice(), event.filter);
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
      filterValue: event.filter.value,
      search: event.filter,
    }));
  };

  const onOpenChange = (setDataFields, moduleData, key, dataFields, dataFieldsValue) => {
    let dataValue =
      dataFields.filterValue && dataFields.filterValue !== ""
        ? filterBy(dataFieldsValue.slice(), dataFields.search)
        : moduleData;
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
    }));
  };

  const handleChangeDropdownValue = (e, name, setDataFields, dataList, valueData, dataFields) => {
    if (valueData && valueData.filter((x) => x.text === "Select All").length > 0) {
      let index = e.value.findIndex((x) => x.text === "Select All");
      e.value.splice(index, 1);
    } else if (e.value && e.value.filter((x) => x.text === "Select All").length > 0 && e.value.length > 1) {
      e.value = [];
      e.value.push({ text: "Select All", id: "Select All" });
    }
    if (e.value.length === 0) {
      e.value.push({ text: "Select All", id: "Select All" });
    }

    handleOnChange(e.value, name, setDataFields, dataList, dataFields);
  };

  const handleBlurDropdown = (setDataFields) => {
    setDataFields((prev) => ({
      ...prev,
      filterValue: "",
      search: "",
    }));
  };
  return (
    <>
      {isBusinessGroupListAPILoading && <LoadingPanel />}
      <div className="Main providerpositionaling">
        {mode === "Default" ? (
          <>
            <div className="content">
              <div className="content-head ">
                <HeaderPage pageProps={pageProps} headerConfig={headerConfig} />
              </div>
              <div className="content-body">
                <>
                  <ul className="Filter-links">
                    <div className="k-form-field-wrap-popup valueDropdown" ref={divRef}>
                      <Label>Specialty:</Label>
                      <MultiSelect
                        name="specialtyDataValue"
                        dataItemKey="id"
                        textField="text"
                        data={specialtyDataFields.specialtyData}
                        filterable
                        value={specialtyDataFields.specialtyDataValue}
                        autoClose={false}
                        filter={specialtyDataFields.filterValue}
                        loading={specialtyDataFields.isSpecialtyDataLoading}
                        disabled={specialtyDataFields.isSpecialtyDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setSpecialtyDataFields,
                            specialtyDataList,
                            "specialtyData",
                            specialtyDataFields,
                            specialtyDataFields.specialtyDataValue
                          )
                        }
                        onChange={(e) =>
                          handleChangeDropdownValue(
                            e,
                            "specialtyDataValue",
                            setSpecialtyDataFields,
                            specialtyDataList,
                            specialtyDataFields.specialtyDataValue,
                            specialtyDataFields
                          )
                        }
                        onFilterChange={(e) =>
                          filterChange(e, setSpecialtyDataFields, specialtyDataList, "specialtyData")
                        }
                        onBlur={() => handleBlurDropdown(setSpecialtyDataFields, "specialtyData")}
                      />
                    </div>
                    <div className="k-form-field-wrap-popup valueDropdown">
                      <Label>Group:</Label>
                      <MultiSelect
                        name="groupDataValue"
                        dataItemKey="id"
                        textField="text"
                        data={groupDataFields.groupData}
                        filterable
                        value={groupDataFields.groupDataValue}
                        autoClose={false}
                        filter={groupDataFields.filterValue}
                        loading={groupDataFields.isGroupDataLoading}
                        disabled={groupDataFields.isGroupDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setGroupDataFields,
                            groupDataList,
                            "groupData",
                            groupDataFields,
                            groupDataFields.groupDataValue
                          )
                        }
                        onChange={(e) =>
                          handleChangeDropdownValue(
                            e,
                            "groupDataValue",
                            setGroupDataFields,
                            groupDataList,
                            groupDataFields.groupDataValue,
                            groupDataFields
                          )
                        }
                        onFilterChange={(e) => filterChange(e, setGroupDataFields, groupDataList, "groupData")}
                        onBlur={() => handleBlurDropdown(setGroupDataFields, "groupData")}
                      />
                    </div>
                    <div className="k-form-field-wrap-popup valueDropdown">
                      <Label>Employee ID:</Label>
                      <MultiSelect
                        name="employeeDataValue"
                        dataItemKey="id"
                        textField="text"
                        data={employeeDataFields.employeeData}
                        filterable
                        filter={employeeDataFields.filterValue}
                        value={employeeDataFields.employeeDataValue}
                        autoClose={false}
                        loading={employeeDataFields.isEmployeeDataLoading}
                        disabled={employeeDataFields.isEmployeeDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setEmployeeDataFields,
                            employeeDataList,
                            "employeeData",
                            employeeDataFields,
                            employeeDataFields.employeeDataValue
                          )
                        }
                        onChange={(e) =>
                          handleChangeDropdownValue(
                            e,
                            "employeeDataValue",
                            setEmployeeDataFields,
                            employeeDataList,
                            employeeDataFields.employeeDataValue,
                            employeeDataFields
                          )
                        }
                        onFilterChange={(e) => filterChange(e, setEmployeeDataFields, employeeDataList, "employeeData")}
                        onBlur={() => handleBlurDropdown(setEmployeeDataFields, "employeeData")}
                      />
                    </div>
                    <div className="k-form-field-wrap-popup valueDropdown">
                      <Label>NPI:</Label>
                      <MultiSelect
                        name="npiDataValue"
                        dataItemKey="id"
                        textField="text"
                        data={npiDataFields.npiData}
                        filterable
                        value={npiDataFields.npiDataValue}
                        autoClose={false}
                        filter={npiDataFields.filterValue}
                        loading={npiDataFields.isNpiDataLoading}
                        disabled={npiDataFields.isNpiDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setNpiDataFields,
                            npiDataList,
                            "npiData",
                            npiDataFields,
                            npiDataFields.npiDataValue
                          )
                        }
                        onChange={(e) =>
                          handleChangeDropdownValue(
                            e,
                            "npiDataValue",
                            setNpiDataFields,
                            npiDataList,
                            npiDataFields.npiDataValue,
                            npiDataFields
                          )
                        }
                        onFilterChange={(e) => filterChange(e, setNpiDataFields, npiDataList, "npiData")}
                        onBlur={() => handleBlurDropdown(setNpiDataFields, "npiData")}
                      />
                    </div>
                    <div className="k-form-field-wrap-popup valueDropdown">
                      <Label>Position Name:</Label>
                      <MultiSelect
                        name="positionDataValue"
                        dataItemKey="id"
                        textField="text"
                        data={positionDataFields.positionData}
                        filterable
                        value={positionDataFields.positionDataValue}
                        autoClose={false}
                        filter={positionDataFields.filterValue}
                        loading={positionDataFields.isPositionDataLoading}
                        disabled={positionDataFields.isPositionDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setPositionDataFields,
                            positionDataList,
                            "positionData",
                            positionDataFields,
                            positionDataFields.positionDataValue
                          )
                        }
                        onChange={(e) =>
                          handleChangeDropdownValue(
                            e,
                            "positionDataValue",
                            setPositionDataFields,
                            positionDataList,
                            positionDataFields.positionDataValue,
                            positionDataFields
                          )
                        }
                        onFilterChange={(e) => filterChange(e, setPositionDataFields, positionDataList, "positionData")}
                        onBlur={() => handleBlurDropdown(setPositionDataFields, "positionData")}
                      />
                    </div>
                    <Button
                      primary
                      className="searchbtn"
                      disabled={
                        specialtyDataFields.isSpecialtyDataLoading ||
                        groupDataFields.isGroupDataLoading ||
                        employeeDataFields.isEmployeeDataLoading ||
                        npiDataFields.isNpiDataLoading ||
                        positionDataFields.isPositionDataLoading
                      }
                      type="submit"
                      onClick={onPositionClick}
                    >
                      <i className="fas fa-search"></i>
                    </Button>
                  </ul>
                  <div className="dtu-file-container padtop15" ref={_ref}>
                    {loading.updateColumn || loading.isSearchLoader || loadState.isUploadState || isAPICalling ? (
                      <LoadingPanel />
                    ) : (
                      false
                    )}
                    {!loading.isSearchLoader && columns.length > 0 && (
                      <CompData
                        key={providerPositionGridKey}
                        isAPICalling={isAPICalling}
                        initialLoading={initialLoading}
                        data={gridData}
                        pageData={pageData}
                        dataCount={memoizedData.length}
                        customColumn={CustomColumn}
                        columns={gridColumns}
                        dataItemKey={DATA_ITEM_KEY}
                        width={"auto"}
                        defaultPageSize={defaultPageSize}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        noDataFound={noDataFound}
                        isExportDataClick={exportDataClick}
                        setExportData={setExportData}
                        exportData={exportData}
                        fileName={
                          isShow.exportAllVersion ? "Export_ProviderPosition_All" : "Export_ProviderPosition_Current"
                        }
                        reorderable={!!pageProps.writeAccess}
                        initialFilter={filterField}
                        initialSort={sortField}
                        resetSort={resetSort}
                        handleColumnMenu={handleColumnMenu}
                        rowRender={customRowRender}
                        columnReorderData={columnReorderData}
                        onSelectionChange={onSelectionChange}
                        getExportData={getExportData}
                        onClearFilter={onClearFilter}
                        handlePageChange={handlePageChange}
                        refreshClick={onRefreshClick}
                        id={"provider-position"}
                        isSelection={isSelection}
                        isDTUUploadReload={isDTUUploadReload}
                        pageDatavs={pageDatavs}
                        module={"provider-position"}
                        defaultPageSizevs={defaultPageSizevs}
                        originalDataCount={dataCount}
                        originalData={data}
                      />
                    )}
                  </div>
                </>
                {isShow.isManageCustomType && (
                  <ManageCustomField visible={isShow.isManageCustomType} setShow={setShow} pageProps={pageProps} />
                )}
              </div>
            </div>
          </>
        ) : mode === "Add" ? (
          <AddProviderPosition
            pageProps={pageProps}
            setMode={setMode}
            isSearchClick={isSearchClick}
            setSearchClick={setSearchClick}
            setAdd={setAdd}
          />
        ) : mode === "Edit" ? (
          <UpdateProviderPosition
            setIsOneEffVersion={setIsOneEffVersion}
            setIsUpdate={setIsUpdate}
            pageProps={pageProps}
            setMode={setMode}
            setEditData={setEditData}
            editData={editData}
            setBackBtnClick={setBackBtnClick}
          />
        ) : (
          false
        )}
      </div>
      {isShow.dtuLogs && (
        <DtuBatchLogs visible={isShow} setShow={setShow} module={"provider_master"} pageProps={pageProps} />
      )}
      {isShow.dtuUpload && (
        <UploadDtu
          visible={isShow}
          setShow={setShow}
          title={"Upload Provider Positions"}
          pageId={"provider_position"}
          setAPICall={onRefreshOnManualUpload}
        />
      )}

      {isShow.viewBusinessGroups && (
        <BusinessGroupPopup
          visible={isShow.viewBusinessGroups}
          setShow={setShow}
          isShow={isShow}
          businessGroupData={viewBusinessGroupsData}
          onColumnMenuChange={handleBusinessGroupColumnMenu}
        />
      )}

      <DialogComponent
        title={confirmationPopup.title}
        onCancelConfirm={onCancelConfirm}
        width={
          (Array.isArray(confirmationPopup?.data)
            ? confirmationPopup.data.length
            : Array.isArray(confirmationPopup?.data?.data)
              ? confirmationPopup.data.data.length
              : 0) > 0
            ? "500px"
            : confirmationPopup.standAloneProvider
              ? "450px"
              : "400px"
        }
        height={"auto"}
        className={
          (Array.isArray(confirmationPopup?.data)
            ? confirmationPopup.data.length
            : Array.isArray(confirmationPopup?.data?.data)
              ? confirmationPopup.data.data.length
              : 0) > 0
            ? "Dialog-Delete-provider-position"
            : confirmationPopup.standAloneProvider
              ? "Dialog-Delete"
              : "Dialog-Delete-provider-position"
        }
        desc={confirmationPopup.desc}
        visible={confirmationPopup.isVisible}
        actionBtn={confirmationPopup.actionBtn}
        titleClassName={confirmationPopup.action === "Delete" ? "delete-confirm" : "unSave-confirm"}
      />
    </>
  );
};

export default React.memo(ProviderPosition);

export const GridButton = (restProps) => {
  return (
    <>
      <Button type="submit" className="cancelbtn" id="grid-incell-cancel">
        Cancel
      </Button>
      <Button type="submit" className="primarybtn" id="grid-incell-update">
        Save
      </Button>
    </>
  );
};
